import { changePasswordRequest } from '@uplift/api';
import { EMPTY_STRING, PASSWORD_MATCHES } from '@uplift/constants';
import { useL10n, useStore } from '@uplift/hooks';
import { FormikHelpers } from 'formik';
import { useUpdateSettingsHandlers } from '../../../../molecules/Forms/Settings/hooks';
import * as Yup from 'yup';

export type ChangePasswordFormType = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

export const useChangePasswordForm = (userId: string) => {
  const l10n = useL10n();
  const { authStore } = useStore();
  const { onFormSubmitSuccess, onFormSubmitError } = useUpdateSettingsHandlers(userId, l10n.providerSettings.password);

  const changePasswordFormik = () => {
    const initialValues: ChangePasswordFormType = {
      currentPassword: EMPTY_STRING,
      newPassword: EMPTY_STRING,
      confirmNewPassword: EMPTY_STRING
    };

    const validationSchema = Yup.object({
      currentPassword: Yup.string().required(l10n.updatePassword.errors.currentRequired),
      newPassword: Yup.string()
        .max(50, l10n.validations.tooLong)
        .min(6, l10n.validations.tooShort)
        .required(l10n.validations.required)
        .matches(PASSWORD_MATCHES, l10n.validations.passwordMatches),
      confirmNewPassword: Yup.string()
        .nullable()
        .oneOf([ Yup.ref('newPassword'), null ], l10n.updatePassword.errors.doNotMatch)
        .required(l10n.updatePassword.errors.confirmEmpty),
    });

    const onSubmit = async (values: ChangePasswordFormType, formikHelpers: FormikHelpers<ChangePasswordFormType>) => {
      try {
        const result = await changePasswordRequest({
          newPassword: values.newPassword,
          oldPassword: values.currentPassword,
        });

        if (result.status === 'error' && result.error === 'Invalid old password') {
          formikHelpers.setFieldError('currentPassword', result.error);
          return;
        }

        if (result.tokens) {
          authStore.setTokens({
            accessToken: result.tokens.accessToken,
            refreshToken: result.tokens.refreshToken,
          });
          authStore.setTokensInLocalStorage();
        }

        onFormSubmitSuccess();
      } catch (error) {
        onFormSubmitError();
      }
    };

    return { initialValues, validationSchema, onSubmit };
  };

  return { changePasswordFormik };
};
